import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { GroupedProjectItems } from './ProjectOptionLabel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Trash2, Save, Layers, PackagePlus, CornerDownRight, FolderPlus, ChevronDown, ChevronRight, Database, PackageSearch, AlertTriangle, FileText, Download, RefreshCw, Copy, Lock, Unlock, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import ConstructionProposalDialog from './ConstructionProposalDialog';
import MarginWarningDialog from './MarginWarningDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const uid = () => Math.random().toString(36).slice(2, 10);

const peso = (n) => `₱${(Number(n) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => (v === '' || v === null || v === undefined ? '' : v);

const rowMat = (it, m) => (Number(it.qty) || 0) * (Number(it.unit_cost_materials) || 0) * (1 + (Number(m) || 0) / 100);
const rowLab = (it, m) => (Number(it.qty) || 0) * (Number(it.unit_cost_labor) || 0) * (1 + (Number(m) || 0) / 100);
const itemMat = (it, m) => (it.subitems && it.subitems.length ? it.subitems.reduce((a, s) => a + rowMat(s, m), 0) : rowMat(it, m));
const itemLab = (it, m) => (it.subitems && it.subitems.length ? it.subitems.reduce((a, s) => a + rowLab(s, m), 0) : rowLab(it, m));
const sectionMat = (sec, m) => sec.items.reduce((a, i) => a + itemMat(i, m), 0);
const sectionLab = (sec, m) => sec.items.reduce((a, i) => a + itemLab(i, m), 0);
const catMat = (cat, m) => (cat.sections || []).reduce((a, s) => a + sectionMat(s, m), 0);
const catLab = (cat, m) => (cat.sections || []).reduce((a, s) => a + sectionLab(s, m), 0);

const newCategory = () => ({ id: uid(), name: 'NEW CATEGORY', collapsed: false, sections: [] });
const newSection = (idx) => ({ id: uid(), item_no: String.fromCharCode(65 + idx), title: 'NEW SECTION', items: [] });
const newItem = (idx) => ({ id: uid(), item_no: `${idx}.00`, scope: '', qty: 1, unit: 'lot', unit_cost_materials: 0, unit_cost_labor: 0, subitems: [] });
const newSub = (parentNo, subs) => {
  const prefix = String(parentNo || '').split('.')[0] || '';
  let maxN = 0;
  (subs || []).forEach(su => {
    const n = parseInt(String(su.item_no || '').split('.')[1], 10);
    if (!isNaN(n)) maxN = Math.max(maxN, n);
  });
  return { id: uid(), item_no: `${prefix}.${String(maxN + 1).padStart(2, '0')}`, scope: '', qty: 1, unit: 'lot', unit_cost_materials: 0, unit_cost_labor: 0 };
};

export default function ProjectBOMTab() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [categories, setCategories] = useState([]);
  const [materialsMargin, setMaterialsMargin] = useState(20);
  const [laborMargin, setLaborMargin] = useState(20);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [matCategories, setMatCategories] = useState([]);
  const [matPrices, setMatPrices] = useState({});
  const [recent, setRecent] = useState([]);
  const [showProposal, setShowProposal] = useState(false);
  const [downloadingBom, setDownloadingBom] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [saveMat, setSaveMat] = useState(null);
  const [pickMat, setPickMat] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState('');
  const [copying, setCopying] = useState(false);

  const reIdCategories = (cats) => (cats || []).map(c => ({
    ...c, id: uid(), collapsed: false,
    sections: (c.sections || []).map(s => ({
      ...s, id: uid(),
      items: (s.items || []).map(it => ({
        ...it, id: uid(),
        subitems: (it.subitems || []).map(su => ({ ...su, id: uid() })),
      })),
    })),
  }));

  const copyBomFromProject = async () => {
    if (!copySource) { toast.error('Select a project to copy from'); return; }
    setCopying(true);
    try {
      const r = await axios.get(`${API_URL}/api/bom/${copySource}`);
      let cats = r.data?.categories;
      if ((!cats || cats.length === 0) && r.data?.sections) cats = [{ name: 'GENERAL', sections: r.data.sections }];
      cats = reIdCategories(cats || []);
      if (cats.length === 0) { toast.error('That project has no BOM to copy'); setCopying(false); return; }
      setCategories(prev => [...prev, ...cats]);
      toast.success(`Appended ${cats.length} categor${cats.length > 1 ? 'ies' : 'y'} — review and click Save`);
      setCopyOpen(false); setCopySource('');
    } catch { toast.error('Failed to copy BOM from that project'); }
    setCopying(false);
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/billing/projects`)
      .then(r => setProjects(r.data || []))
      .catch(() => toast.error('Failed to load projects'));
  }, []);

  const loadBom = useCallback(async (pid) => {
    if (!pid) { setCategories([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/bom/${pid}`);
      const cats = r.data?.categories;
      if (Array.isArray(cats) && cats.length) setCategories(cats);
      else if (Array.isArray(r.data?.sections) && r.data.sections.length) {
        setCategories([{ id: uid(), name: 'GENERAL', collapsed: false, sections: r.data.sections }]);
      } else setCategories([]);
      setMaterialsMargin(r.data?.materials_margin ?? 20);
      setLaborMargin(r.data?.labor_margin ?? 20);
      setIsLocked(!!r.data?.is_locked);
    } catch { toast.error('Failed to load bill of materials'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadBom(projectId); }, [projectId, loadBom]);

  const save = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/bom/${projectId}`, {
        categories, materials_margin: Number(materialsMargin) || 0, labor_margin: Number(laborMargin) || 0, is_locked: isLocked,
      });
      toast.success('Bill of Materials saved');
      fetchRecent();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleLock = async () => {
    if (!projectId) return;
    const next = !isLocked;
    setIsLocked(next);
    try {
      await axios.post(`${API_URL}/api/bom/${projectId}`, {
        categories, materials_margin: Number(materialsMargin) || 0, labor_margin: Number(laborMargin) || 0, is_locked: next,
      });
      toast.success(next ? 'BOM locked — unit costs will no longer auto-update from the Material Database' : 'BOM unlocked — prices can be refreshed again');
    } catch { setIsLocked(!next); toast.error('Failed to update lock state'); }
  };

  const downloadBomExcel = async () => {
    if (!projectId) return;
    setDownloadingXlsx(true);
    try {
      await axios.post(`${API_URL}/api/bom/${projectId}`, {
        categories, materials_margin: Number(materialsMargin) || 0, labor_margin: Number(laborMargin) || 0, is_locked: isLocked,
      });
      const r = await axios.get(`${API_URL}/api/bom/${projectId}/excel`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'BOM.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download BOM Excel'); }
    finally { setDownloadingXlsx(false); }
  };

  const downloadBomPdf = async () => {
    if (!projectId) return;
    setDownloadingBom(true);
    try {
      await axios.post(`${API_URL}/api/bom/${projectId}`, {
        categories, materials_margin: Number(materialsMargin) || 0, labor_margin: Number(laborMargin) || 0, is_locked: isLocked,
      });
      const r = await axios.get(`${API_URL}/api/bom/${projectId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'BOM.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download BOM PDF'); }
    finally { setDownloadingBom(false); }
  };

  const [marginWarn, setMarginWarn] = useState({ open: false, action: null });
  const guardMargins = (action) => {
    const zero = (Number(materialsMargin) || 0) === 0 || (Number(laborMargin) || 0) === 0;
    if (zero) setMarginWarn({ open: true, action });
    else action();
  };
  const closeMarginWarn = () => setMarginWarn({ open: false, action: null });
  const confirmMarginWarn = () => { const a = marginWarn.action; closeMarginWarn(); if (a) a(); };

  const setCat = (cid, updater) => setCategories(cs => cs.map(c => c.id === cid ? updater(c) : c));
  const mapSection = (c, sid, fn) => ({ ...c, sections: c.sections.map(s => s.id === sid ? fn(s) : s) });
  const mapItem = (s, iid, fn) => ({ ...s, items: s.items.map(i => i.id === iid ? fn(i) : i) });

  const addCategory = () => setCategories(cs => [...cs, newCategory()]);
  const delCategory = (cid) => setCategories(cs => cs.filter(c => c.id !== cid));
  const updCategory = (cid, patch) => setCat(cid, c => ({ ...c, ...patch }));
  const addSection = (cid) => setCat(cid, c => ({ ...c, sections: [...c.sections, newSection(c.sections.length)] }));
  const delSection = (cid, sid) => setCat(cid, c => ({ ...c, sections: c.sections.filter(s => s.id !== sid) }));
  const updSection = (cid, sid, patch) => setCat(cid, c => mapSection(c, sid, s => ({ ...s, ...patch })));
  const addItem = (cid, sid) => setCat(cid, c => mapSection(c, sid, s => ({ ...s, items: [...s.items, newItem(s.items.length + 1)] })));
  const renumberItems = (items) => items.map((it, idx) => ({
    ...it,
    item_no: `${idx + 1}.00`,
    subitems: (it.subitems || []).map((su, si) => ({ ...su, item_no: `${idx + 1}.${String(si + 1).padStart(2, '0')}` })),
  }));
  const insertItemBelow = (cid, sid, iid) => setCat(cid, c => mapSection(c, sid, s => {
    const idx = s.items.findIndex(i => i.id === iid);
    const arr = [...s.items];
    arr.splice(idx + 1, 0, newItem(arr.length + 1));
    return { ...s, items: renumberItems(arr) };
  }));
  const insertSubBelow = (cid, sid, iid, subId) => setCat(cid, c => mapSection(c, sid, s => mapItem(s, iid, i => {
    const subs = [...(i.subitems || [])];
    const idx = subs.findIndex(su => su.id === subId);
    const prefix = String(i.item_no || '').split('.')[0] || '';
    subs.splice(idx + 1, 0, { id: uid(), item_no: '', scope: '', qty: 1, unit: 'lot', unit_cost_materials: 0, unit_cost_labor: 0 });
    return { ...i, subitems: subs.map((su, si) => ({ ...su, item_no: `${prefix}.${String(si + 1).padStart(2, '0')}` })) };
  })));
  const delItem = (cid, sid, iid) => setCat(cid, c => mapSection(c, sid, s => ({ ...s, items: s.items.filter(i => i.id !== iid) })));
  const updItem = (cid, sid, iid, patch) => setCat(cid, c => mapSection(c, sid, s => mapItem(s, iid, i => ({ ...i, ...patch }))));
  const addSub = (cid, sid, iid) => setCat(cid, c => mapSection(c, sid, s => mapItem(s, iid, i => ({ ...i, subitems: [...(i.subitems || []), newSub(i.item_no, i.subitems || [])] }))));
  const delSub = (cid, sid, iid, subId) => setCat(cid, c => mapSection(c, sid, s => mapItem(s, iid, i => ({ ...i, subitems: (i.subitems || []).filter(su => su.id !== subId) }))));
  const updSub = (cid, sid, iid, subId, patch) => setCat(cid, c => mapSection(c, sid, s => mapItem(s, iid, i => ({ ...i, subitems: (i.subitems || []).map(su => su.id === subId ? { ...su, ...patch } : su) }))));

  const addSubFromMaterial = (cid, sid, iid, m, afterSubId = null) => setCat(cid, c => mapSection(c, sid, s => mapItem(s, iid, i => {
    const subs = [...(i.subitems || [])];
    const su = newSub(i.item_no, subs);
    su.scope = m.item_name || '';
    su.unit = m.unit_type || 'lot';
    su.unit_cost_materials = Number(m.unit_cost) || 0;
    su.material_id = m.id;
    su.source_unit_cost = Number(m.unit_cost) || 0;
    const prefix = String(i.item_no || '').split('.')[0] || '';
    if (afterSubId) {
      const idx = subs.findIndex(x => x.id === afterSubId);
      subs.splice(idx >= 0 ? idx + 1 : subs.length, 0, su);
    } else {
      subs.push(su);
    }
    return { ...i, subitems: subs.map((x, si) => ({ ...x, item_no: `${prefix}.${String(si + 1).padStart(2, '0')}` })) };
  })));

  const fetchMatPrices = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/supply-chain/materials`);
      const map = {};
      (r.data || []).forEach(m => { map[m.id] = Number(m.unit_cost) || 0; });
      setMatPrices(map);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { fetchMatPrices(); }, [fetchMatPrices]);

  const syncMaterialPrices = async () => {
    if (isLocked) { toast.info('BOM is locked — unlock it to refresh prices from the Material Database'); return; }
    try {
      const r = await axios.get(`${API_URL}/api/supply-chain/materials`);
      const map = {};
      const matById = {};
      const byName = {};
      (r.data || []).forEach(m => {
        map[m.id] = Number(m.unit_cost) || 0;
        matById[m.id] = m;
        const key = (m.item_name || '').trim().toLowerCase();
        if (!key) return;
        const existing = byName[key];
        if (!existing || (Number(m.unit_cost) > 0 && !(Number(existing.unit_cost) > 0))) byName[key] = m;
      });
      setMatPrices(map);
      let updated = 0;
      const next = categories.map(c => ({
        ...c,
        sections: (c.sections || []).map(s => ({
          ...s,
          items: (s.items || []).map(it => ({
            ...it,
            subitems: (it.subitems || []).map(su => {
              let mat;
              if (su.material_id && matById[su.material_id]) mat = matById[su.material_id];
              else mat = byName[(su.scope || '').trim().toLowerCase()];
              if (!mat) return su;
              const matchCost = Number(mat.unit_cost) || 0;
              const matchUnit = mat.unit_type || su.unit || '';
              const costChanged = Number(matchCost) !== Number(su.unit_cost_materials || 0);
              const unitChanged = !!(mat.unit_type) && mat.unit_type !== (su.unit || '');
              const needsLink = su.material_id !== mat.id;
              if (costChanged || unitChanged || needsLink) {
                if (costChanged || unitChanged) updated += 1;
                return { ...su, unit_cost_materials: matchCost, unit: matchUnit, material_id: mat.id, source_unit_cost: matchCost };
              }
              return su;
            }),
          })),
        })),
      }));
      setCategories(next);
      toast.success(updated > 0
        ? `Refreshed — ${updated} item${updated > 1 ? 's' : ''} updated from the Material Database`
        : 'Prices are already up to date with the Material Database');
    } catch { toast.error('Failed to refresh prices'); }
  };

  const fetchRecent = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/api/bom-recent`); setRecent(r.data || []); }
    catch { /* noop */ }
  }, []);
  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  const fetchMatCategories = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/api/supply-chain/material-categories`); setMatCategories(r.data || []); }
    catch { /* noop */ }
  }, []);

  const openSaveMat = (cid, sid, iid, sub) => { fetchMatCategories(); setSaveMat({ cid, sid, iid, sub, category: '' }); };
  const confirmSaveMat = async () => {
    if (!saveMat) return;
    const cat = (saveMat.category || '').trim();
    const name = (saveMat.sub.scope || '').trim();
    if (!cat) { toast.error('Material Category is required'); return; }
    if (!name) { toast.error('Item name (scope) is required'); return; }
    try {
      const chk = await axios.get(`${API_URL}/api/supply-chain/materials`, { params: { category: cat } });
      const dup = (chk.data || []).some(m => (m.item_name || '').trim().toLowerCase() === name.toLowerCase());
      if (dup && !window.confirm(`"${name}" already exists in the "${cat}" category. Add it anyway?`)) return;
    } catch { /* noop — proceed if the check fails */ }
    try {
      await axios.post(`${API_URL}/api/supply-chain/materials`, {
        material_category: cat, item_name: name,
        unit_type: saveMat.sub.unit || '', unit_cost: Number(saveMat.sub.unit_cost_materials) || 0,
      });
      toast.success('Added to Material Database');
      setSaveMat(null);
      fetchMatPrices();
    } catch { toast.error('Failed to save material'); }
  };

  const openPickMat = (cid, sid, iid) => { fetchMatCategories(); setPickMat({ cid, sid, iid, category: '', materials: [], loading: false }); };
  const loadPickMaterials = async (category) => {
    setPickMat(p => ({ ...p, category, loading: true, materials: [] }));
    try {
      const r = await axios.get(`${API_URL}/api/supply-chain/materials`, { params: { category } });
      setPickMat(p => ({ ...p, materials: r.data || [], loading: false }));
    } catch { setPickMat(p => ({ ...p, loading: false })); toast.error('Failed to load materials'); }
  };
  const chooseMaterial = (m) => {
    if (!pickMat) return;
    addSubFromMaterial(pickMat.cid, pickMat.sid, pickMat.iid, m, pickMat.afterSubId);
    toast.success(`Added "${m.item_name}" as sub item`);
    setPickMat(null);
  };

  const grand = categories.reduce((acc, cat) => {
    acc.mat += catMat(cat, materialsMargin); acc.lab += catLab(cat, laborMargin); return acc;
  }, { mat: 0, lab: 0 });
  const grandTotal = grand.mat + grand.lab;

  const numInput = (val, onChange, testid, cls = 'text-right') => (
    <Input type="number" value={num(val)} onChange={e => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
      className={`h-7 text-xs ${cls}`} data-testid={testid} />
  );

  const TableHead = () => (
    <thead>
      <tr className="bg-slate-800 text-white">
        <th rowSpan={2} className="p-2 border border-slate-600 w-16">Item No.</th>
        <th rowSpan={2} className="p-2 border border-slate-600 text-left min-w-[240px]">Scope of Work</th>
        <th colSpan={2} className="p-2 border border-slate-600">Quantity</th>
        <th colSpan={2} className="p-2 border border-slate-600">Unit Cost</th>
        <th colSpan={3} className="p-2 border border-slate-600">Total Cost</th>
        <th rowSpan={2} className="p-2 border border-slate-600 min-w-[160px]">Notes</th>
        <th rowSpan={2} className="p-2 border border-slate-600 w-16"></th>
      </tr>
      <tr className="bg-slate-700 text-white">
        <th className="p-1.5 border border-slate-600 w-16">No.</th>
        <th className="p-1.5 border border-slate-600 w-16">Unit</th>
        <th className="p-1.5 border border-slate-600 w-24">Materials</th>
        <th className="p-1.5 border border-slate-600 w-24">Labor</th>
        <th className="p-1.5 border border-slate-600 w-28">Materials</th>
        <th className="p-1.5 border border-slate-600 w-28">Labor</th>
        <th className="p-1.5 border border-slate-600 w-28">Total</th>
      </tr>
    </thead>
  );

  const renderSection = (cid, sec) => {
    const secMat = sectionMat(sec, materialsMargin);
    const secLab = sectionLab(sec, laborMargin);
    return (
      <React.Fragment key={sec.id}>
        <tr className="bg-blue-50 dark:bg-blue-950/40 font-semibold">
          <td className="p-1 border">
            <Input value={sec.item_no} onChange={e => updSection(cid, sec.id, { item_no: e.target.value })}
              className="h-7 text-xs text-center font-bold" data-testid={`bom-section-no-${sec.id}`} />
          </td>
          <td className="p-1 border" colSpan={5}>
            <Input value={sec.title} onChange={e => updSection(cid, sec.id, { title: e.target.value.toUpperCase() })}
              className="h-7 text-xs font-bold uppercase" data-testid={`bom-section-title-${sec.id}`} />
          </td>
          <td className="p-1 border text-right font-bold">{peso(secMat)}</td>
          <td className="p-1 border text-right font-bold">{peso(secLab)}</td>
          <td className="p-1 border text-right font-bold">{peso(secMat + secLab)}</td>
          <td className="p-1 border"></td>
          <td className="p-1 border text-center">
            <button onClick={() => delSection(cid, sec.id)} className="text-red-500 hover:text-red-700" data-testid={`bom-del-section-${sec.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </td>
        </tr>
        {sec.items.map(it => {
          const hasSubs = (it.subitems || []).length > 0;
          const pMat = itemMat(it, materialsMargin);
          const pLab = itemLab(it, laborMargin);
          return (
            <React.Fragment key={it.id}>
              <tr className={hasSubs ? 'bg-amber-50 dark:bg-amber-950/20 font-medium' : 'hover:bg-muted/40'}>
                <td className="p-1 border">
                  <Input value={it.item_no} onChange={e => updItem(cid, sec.id, it.id, { item_no: e.target.value })}
                    className="h-7 text-xs text-center" data-testid={`bom-item-no-${it.id}`} />
                </td>
                <td className="p-1 border">
                  <Input value={it.scope} onChange={e => updItem(cid, sec.id, it.id, { scope: e.target.value })}
                    placeholder="Description" className="h-7 text-xs" data-testid={`bom-item-scope-${it.id}`} />
                </td>
                {hasSubs ? (
                  <>
                    <td className="p-1 border text-center text-muted-foreground">—</td>
                    <td className="p-1 border text-center text-muted-foreground">—</td>
                    <td className="p-1 border text-center text-muted-foreground">—</td>
                    <td className="p-1 border text-center text-muted-foreground">—</td>
                  </>
                ) : (
                  <>
                    <td className="p-1 border">{numInput(it.qty, v => updItem(cid, sec.id, it.id, { qty: v }), `bom-item-qty-${it.id}`, 'text-center')}</td>
                    <td className="p-1 border">
                      <Input value={it.unit} onChange={e => updItem(cid, sec.id, it.id, { unit: e.target.value })}
                        className="h-7 text-xs text-center" data-testid={`bom-item-unit-${it.id}`} />
                    </td>
                    <td className="p-1 border">{numInput(it.unit_cost_materials, v => updItem(cid, sec.id, it.id, { unit_cost_materials: v }), `bom-item-umat-${it.id}`)}</td>
                    <td className="p-1 border">{numInput(it.unit_cost_labor, v => updItem(cid, sec.id, it.id, { unit_cost_labor: v }), `bom-item-ulab-${it.id}`)}</td>
                  </>
                )}
                <td className="p-1 border text-right">{peso(pMat)}</td>
                <td className="p-1 border text-right">{peso(pLab)}</td>
                <td className="p-1 border text-right font-medium">{peso(pMat + pLab)}</td>
                <td className="p-1 border">
                  <Input value={it.notes || ''} onChange={e => updItem(cid, sec.id, it.id, { notes: e.target.value })}
                    placeholder="Notes" className="h-7 text-xs" data-testid={`bom-item-notes-${it.id}`} />
                </td>
                <td className="p-1 border text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <button onClick={() => insertItemBelow(cid, sec.id, it.id)} className="text-blue-500 hover:text-blue-700" title="Add line item below" data-testid={`bom-insert-item-${it.id}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => addSub(cid, sec.id, it.id)} className="text-primary hover:text-primary/70" title="Add sub item" data-testid={`bom-add-sub-${it.id}`}>
                      <CornerDownRight className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => delItem(cid, sec.id, it.id)} className="text-red-400 hover:text-red-600" title="Delete line item" data-testid={`bom-del-item-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
              {(it.subitems || []).map(su => (
                <tr key={su.id} className="hover:bg-muted/40">
                  <td className="p-1 border pl-4">
                    <Input value={su.item_no} onChange={e => updSub(cid, sec.id, it.id, su.id, { item_no: e.target.value })}
                      className="h-7 text-xs text-center" data-testid={`bom-sub-no-${su.id}`} />
                  </td>
                  <td className="p-1 border">
                    <div className="flex items-center gap-1">
                      <CornerDownRight className="w-3 h-3 text-muted-foreground shrink-0 ml-1" />
                      <Input value={su.scope} onChange={e => updSub(cid, sec.id, it.id, su.id, { scope: e.target.value })}
                        placeholder="Sub description" className="h-7 text-xs" data-testid={`bom-sub-scope-${su.id}`} />
                    </div>
                  </td>
                  <td className="p-1 border">{numInput(su.qty, v => updSub(cid, sec.id, it.id, su.id, { qty: v }), `bom-sub-qty-${su.id}`, 'text-center')}</td>
                  <td className="p-1 border">
                    <Input value={su.unit} onChange={e => updSub(cid, sec.id, it.id, su.id, { unit: e.target.value })}
                      className="h-7 text-xs text-center" data-testid={`bom-sub-unit-${su.id}`} />
                  </td>
                  <td className="p-1 border">
                    <div className="flex items-center gap-1">
                      {numInput(su.unit_cost_materials, v => updSub(cid, sec.id, it.id, su.id, { unit_cost_materials: v }), `bom-sub-umat-${su.id}`)}
                      {!isLocked && su.material_id && matPrices[su.material_id] !== undefined && Number(matPrices[su.material_id]) !== Number(su.unit_cost_materials || 0) && (
                        <button onClick={() => updSub(cid, sec.id, it.id, su.id, { unit_cost_materials: matPrices[su.material_id] })}
                          className="text-amber-500 hover:text-amber-700 shrink-0"
                          title={`Database price changed to ${peso(matPrices[su.material_id])} — click to update`}
                          data-testid={`bom-sub-drift-${su.id}`}>
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="p-1 border">{numInput(su.unit_cost_labor, v => updSub(cid, sec.id, it.id, su.id, { unit_cost_labor: v }), `bom-sub-ulab-${su.id}`)}</td>
                  <td className="p-1 border text-right">{peso(rowMat(su, materialsMargin))}</td>
                  <td className="p-1 border text-right">{peso(rowLab(su, laborMargin))}</td>
                  <td className="p-1 border text-right font-medium">{peso(rowMat(su, materialsMargin) + rowLab(su, laborMargin))}</td>
                  <td className="p-1 border">
                    <Input value={su.notes || ''} onChange={e => updSub(cid, sec.id, it.id, su.id, { notes: e.target.value })}
                      placeholder="Notes" className="h-7 text-xs" data-testid={`bom-sub-notes-${su.id}`} />
                  </td>
                  <td className="p-1 border text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => insertSubBelow(cid, sec.id, it.id, su.id)} className="text-blue-500 hover:text-blue-700" title="Add blank sub item below" data-testid={`bom-insert-sub-${su.id}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => openPickMat(cid, sec.id, it.id, su.id)} className="text-indigo-500 hover:text-indigo-700" title="Insert item from Database below" data-testid={`bom-insert-sub-db-${su.id}`}>
                        <PackageSearch className="w-3 h-3" />
                      </button>
                      <button onClick={() => openSaveMat(cid, sec.id, it.id, su)} className="text-emerald-500 hover:text-emerald-700" title="Add this item to Material Database" data-testid={`bom-sub-to-db-${su.id}`}>
                        <Database className="w-3 h-3" />
                      </button>
                      <button onClick={() => delSub(cid, sec.id, it.id, su.id)} className="text-red-400 hover:text-red-600" title="Delete sub item" data-testid={`bom-del-sub-${su.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
        <tr>
          <td colSpan={11} className="p-1 border-b">
            <Button variant="ghost" size="sm" className="h-7 text-[11px] text-primary" onClick={() => addItem(cid, sec.id)} data-testid={`bom-add-item-${sec.id}`}>
              <PackagePlus className="w-3 h-3 mr-1" /> Add line item
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-[11px] text-primary ml-1"
              disabled={sec.items.length === 0}
              onClick={() => sec.items.length && addSub(cid, sec.id, sec.items[sec.items.length - 1].id)}
              data-testid={`bom-add-sub-footer-${sec.id}`}>
              <CornerDownRight className="w-3 h-3 mr-1" /> Add sub item
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-[11px] text-emerald-600 ml-1"
              disabled={sec.items.length === 0}
              onClick={() => sec.items.length && openPickMat(cid, sec.id, sec.items[sec.items.length - 1].id)}
              data-testid={`bom-add-from-db-${sec.id}`}>
              <PackageSearch className="w-3 h-3 mr-1" /> Add Item from Database
            </Button>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  return (
    <Card data-testid="project-bom-tab">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary" /> Project Bill of Materials
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="w-full sm:w-[280px]" data-testid="bom-project-selector">
                <SelectValue placeholder="Choose a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.length === 0
                  ? <SelectItem value="none" disabled>No projects found</SelectItem>
                  : <GroupedProjectItems projects={projects} valueKey="project_id" />}
              </SelectContent>
            </Select>
            <Button onClick={save} disabled={!projectId || saving} data-testid="bom-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" onClick={() => { setCopySource(''); setCopyOpen(true); }} disabled={!projectId} data-testid="bom-copy-btn">
              <Copy className="w-4 h-4 mr-1" /> Copy from project
            </Button>
            <Button variant="secondary" onClick={() => setShowProposal(true)} disabled={!projectId} data-testid="bom-create-proposal-btn">
              <FileText className="w-4 h-4 mr-1" /> Create a Construction Proposal
            </Button>
            <Button variant="outline" onClick={() => guardMargins(downloadBomPdf)} disabled={!projectId || downloadingBom} data-testid="bom-download-pdf-btn">
              <Download className="w-4 h-4 mr-1" /> {downloadingBom ? 'Preparing...' : 'Download BOM PDF'}
            </Button>
            <Button variant="outline" onClick={() => guardMargins(downloadBomExcel)} disabled={!projectId || downloadingXlsx} data-testid="bom-download-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> {downloadingXlsx ? 'Preparing...' : 'Export Excel'}
            </Button>
            <Button variant={isLocked ? 'default' : 'outline'} onClick={toggleLock} disabled={!projectId}
              className={isLocked ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''} data-testid="bom-lock-btn">
              {isLocked ? <><Lock className="w-4 h-4 mr-1" /> Locked</> : <><Unlock className="w-4 h-4 mr-1" /> Lock BOM</>}
            </Button>
            <Button variant="outline" onClick={syncMaterialPrices} disabled={!projectId || isLocked} data-testid="bom-refresh-prices-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh Prices
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {recent.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="bom-recent-projects">
            <span className="text-xs font-semibold text-muted-foreground">Recently edited:</span>
            {recent.map(r => (
              <button key={r.project_id} onClick={() => setProjectId(r.project_id)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${projectId === r.project_id ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}
                data-testid={`bom-recent-${r.project_id}`}>{r.project_name}</button>
            ))}
          </div>
        )}
        {!projectId ? (
          <p className="text-muted-foreground text-sm py-10 text-center">Select a project to build its bill of materials.</p>
        ) : loading ? (
          <p className="text-muted-foreground text-sm py-10 text-center">Loading...</p>
        ) : (
          <form className="space-y-4" autoComplete="off" onSubmit={e => e.preventDefault()}>
            <div className="flex flex-wrap items-end gap-6 p-3 border rounded-md bg-muted/40">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Materials Margin %</Label>
                <Input type="number" value={num(materialsMargin)} onChange={e => setMaterialsMargin(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  className="h-8 w-28 text-sm" data-testid="bom-materials-margin" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Labor Margin %</Label>
                <Input type="number" value={num(laborMargin)} onChange={e => setLaborMargin(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  className="h-8 w-28 text-sm" data-testid="bom-labor-margin" />
              </div>
              <p className="text-xs text-muted-foreground pb-1">Margins apply to all line items and sub-items.</p>
            </div>

            {isLocked && (
              <div className="flex items-center gap-2 p-2.5 border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 rounded-md text-xs text-amber-800 dark:text-amber-300" data-testid="bom-locked-banner">
                <Lock className="w-4 h-4 shrink-0" />
                <span>This BOM is <b>locked</b>. Unit costs will no longer auto-update from the Material Database. Manual edits are still allowed. Click <b>Locked</b> to unlock.</span>
              </div>
            )}

            {categories.length === 0 && (
              <p className="text-muted-foreground text-sm py-8 text-center border rounded-md">No categories yet. Add a category to begin.</p>
            )}

            {categories.map(cat => {
              const cMat = catMat(cat, materialsMargin);
              const cLab = catLab(cat, laborMargin);
              return (
                <div key={cat.id} className="border rounded-md overflow-hidden" data-testid={`bom-category-${cat.id}`}>
                  <div className="flex items-center gap-2 bg-slate-900 text-white px-3 py-2">
                    <button onClick={() => updCategory(cat.id, { collapsed: !cat.collapsed })}
                      className="hover:text-primary shrink-0" data-testid={`bom-category-toggle-${cat.id}`}>
                      {cat.collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <Input value={cat.name} onChange={e => updCategory(cat.id, { name: e.target.value.toUpperCase() })}
                      className="h-8 text-sm font-bold uppercase bg-transparent border-slate-700 text-white max-w-md"
                      data-testid={`bom-category-name-${cat.id}`} />
                    <div className="ml-auto flex items-center gap-4">
                      <span className="text-sm font-semibold" data-testid={`bom-category-total-${cat.id}`}>{peso(cMat + cLab)}</span>
                      <button onClick={() => delCategory(cat.id)} className="text-red-400 hover:text-red-300" data-testid={`bom-del-category-${cat.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {!cat.collapsed && (
                    <div className="p-2 space-y-2">
                      <div className="overflow-x-auto border rounded-md">
                        <table className="w-full text-xs border-collapse min-w-[1220px]">
                          <TableHead />
                          <tbody>
                            {(cat.sections || []).length === 0 && (
                              <tr><td colSpan={11} className="p-4 text-center text-muted-foreground">No sections yet. Add a section.</td></tr>
                            )}
                            {(cat.sections || []).map(sec => renderSection(cat.id, sec))}
                          </tbody>
                        </table>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => addSection(cat.id)} data-testid={`bom-add-section-${cat.id}`}>
                        <Plus className="w-4 h-4 mr-1" /> Add Section
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Button variant="default" size="sm" onClick={addCategory} data-testid="bom-add-category-btn">
                <FolderPlus className="w-4 h-4 mr-1" /> Add New Category
              </Button>
              <div className="flex items-center gap-6 bg-slate-800 text-white rounded-md px-4 py-2 text-sm font-bold">
                <span>GRAND TOTAL</span>
                <span>Materials: <span data-testid="bom-grand-materials">{peso(grand.mat)}</span></span>
                <span>Labor: <span data-testid="bom-grand-labor">{peso(grand.lab)}</span></span>
                <span className="text-primary-foreground">Total: <span data-testid="bom-grand-total">{peso(grandTotal)}</span></span>
              </div>
            </div>
          </form>
        )}
      </CardContent>

      <Dialog open={!!saveMat} onOpenChange={(o) => !o && setSaveMat(null)}>
        <DialogContent data-testid="bom-save-mat-dialog">
          <DialogHeader><DialogTitle>Add to Material Database</DialogTitle></DialogHeader>
          {saveMat && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Material Category</Label>
                <Select value={saveMat.newCat ? '__new__' : (saveMat.category || '')}
                  onValueChange={v => v === '__new__'
                    ? setSaveMat(s => ({ ...s, newCat: true, category: '' }))
                    : setSaveMat(s => ({ ...s, newCat: false, category: v }))}>
                  <SelectTrigger data-testid="bom-save-mat-category-select"><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {matCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__new__">+ Add new category</SelectItem>
                  </SelectContent>
                </Select>
                {saveMat.newCat && (
                  <Input value={saveMat.category} autoComplete="off" className="mt-2"
                    onChange={e => setSaveMat(s => ({ ...s, category: e.target.value }))}
                    placeholder="New category name" data-testid="bom-save-mat-category" />
                )}
              </div>
              <div className="rounded-md border p-3 bg-muted/40 space-y-2 text-sm">
                <p className="text-xs text-muted-foreground">Please double-check the extracted details before saving:</p>
                <div className="grid grid-cols-3 gap-2">
                  <div><span className="text-xs text-muted-foreground">Item name</span><p className="font-medium" data-testid="bom-save-mat-name">{saveMat.sub.scope || '—'}</p></div>
                  <div><span className="text-xs text-muted-foreground">Unit type</span><p className="font-medium" data-testid="bom-save-mat-unit">{saveMat.sub.unit || '—'}</p></div>
                  <div><span className="text-xs text-muted-foreground">Unit cost</span><p className="font-medium" data-testid="bom-save-mat-cost">{peso(saveMat.sub.unit_cost_materials)}</p></div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveMat(null)} data-testid="bom-save-mat-cancel">Cancel</Button>
            <Button onClick={confirmSaveMat} data-testid="bom-save-mat-proceed">Proceed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pickMat} onOpenChange={(o) => !o && setPickMat(null)}>
        <DialogContent data-testid="bom-pick-mat-dialog">
          <DialogHeader><DialogTitle>Add Item from Database</DialogTitle></DialogHeader>
          {pickMat && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Material Category</Label>
                <Select value={pickMat.category} onValueChange={loadPickMaterials}>
                  <SelectTrigger data-testid="bom-pick-mat-category"><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {matCategories.length === 0
                      ? <SelectItem value="none" disabled>No categories yet</SelectItem>
                      : matCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="max-h-72 overflow-y-auto border rounded-md">
                {pickMat.loading ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">Loading...</p>
                ) : !pickMat.category ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">Select a category to view materials.</p>
                ) : pickMat.materials.length === 0 ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">No materials in this category.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr><th className="p-2 text-left">Item name</th><th className="p-2 text-left w-20">Unit</th><th className="p-2 text-right w-28">Unit Cost</th><th className="w-16"></th></tr>
                    </thead>
                    <tbody>
                      {pickMat.materials.map(m => (
                        <tr key={m.id} className="border-t hover:bg-muted/40" data-testid={`bom-pick-mat-row-${m.id}`}>
                          <td className="p-2">{m.item_name}</td>
                          <td className="p-2">{m.unit_type || '—'}</td>
                          <td className="p-2 text-right">{peso(m.unit_cost)}</td>
                          <td className="p-2 text-center">
                            <Button size="sm" variant="ghost" className="h-6 text-[11px] text-primary" onClick={() => chooseMaterial(m)} data-testid={`bom-pick-mat-select-${m.id}`}>Select</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickMat(null)} data-testid="bom-pick-mat-cancel">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConstructionProposalDialog projectId={projectId} open={showProposal} onOpenChange={setShowProposal} materialsMargin={materialsMargin} laborMargin={laborMargin} />
      <MarginWarningDialog open={marginWarn.open} onCancel={closeMarginWarn} onConfirm={confirmMarginWarn} materialsMargin={materialsMargin} laborMargin={laborMargin} context="the BOM" />

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent data-testid="bom-copy-dialog">
          <DialogHeader><DialogTitle>Copy BOM from another project</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Source project</Label>
            <Select value={copySource} onValueChange={setCopySource}>
              <SelectTrigger data-testid="bom-copy-source-select"><SelectValue placeholder="Choose a project to copy from" /></SelectTrigger>
              <SelectContent>
                <GroupedProjectItems projects={projects.filter(p => p.project_id !== projectId)} valueKey="project_id" />
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">The source project's categories, sections and items will be <b>appended</b> to this BOM (new copies — the original is untouched). Review and click Save afterwards.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)} data-testid="bom-copy-cancel">Cancel</Button>
            <Button onClick={copyBomFromProject} disabled={!copySource || copying} data-testid="bom-copy-confirm">
              <Copy className="w-4 h-4 mr-1" /> {copying ? 'Copying...' : 'Copy & Append'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
